/**
 * Полотно карты: единственное место, где живёт maplibre.
 *
 * Тайлов нет вовсе. Стиль собран объектом (`version: 8`, пустые `sources`), без
 * `glyphs` и `sprite` — значит, ни одного символьного слоя и ни одной подписи на
 * самой карте: рисовать текст нечем, а тянуть шрифты из сети экран не должен.
 * Отсюда два следствия: карта работает офлайн и не тащит чужую лицензию, а все
 * названия живут в подсказке и легенде рядом с полотном.
 *
 * Компонент не знает, откуда берутся данные, и не ходит в API: он получает уже
 * отобранные коллекции и сообщает наружу рамку, зум и выбранный контур.
 *
 * Два дополнения фазы 2 плана `astra-on-canon` (решение 9: движок один):
 *
 * * **язык Astra** (`astra`): заливка по слою из `astra/map/paint.ts`,
 *   штриховки, обводки наблюдения и «без отчёта», выбор — слоями
 *   `astraStyle.ts`. Кадры экранов Astra рисует это же полотно; второго
 *   движка карты в продукте нет;
 * * **переключаемая подложка** (`basemap.ts`, `agroai-gpo.7.10`): по
 *   умолчанию выключена, и тогда поведение карты прежнее — ни одного
 *   внешнего запроса. Отказ подложки — плашка «подложка недоступна», а не
 *   отказ карты.
 */

import {
  MapLibreMap,
  Marker,
  NavigationControl,
  setWorkerUrl,
  type DataDrivenPropertyValueSpecification,
  type FilterSpecification,
  type GeoJSONSource,
  type MapLayerMouseEvent,
  type StyleSpecification,
} from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import workerUrl from "maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url";
import type { FeatureCollection } from "geojson";
import { useEffect, useReducer, useRef, useState, type ReactNode } from "react";
import { ToggleButton, ToggleButtonGroup } from "react-aria-components";
import type { ContourFeatureProps } from "../../api/client";
import {
  boundsToBbox,
  contourTip,
  cropColorExpression,
  massifColorExpression,
  viewZoom,
  type Bbox,
  type CropPaletteEntry,
  type LayerData,
  type MapView,
  type MassifPaletteEntry,
  type ReferenceProps,
  type Thematic,
} from "./mapModel";
import {
  tierLineLayers,
  TIER_COLOR_TOKENS,
  type TierColorToken,
} from "./tierRegistry";
import { HATCH_DEMO, HATCH_PENDING, hatch45, hatchCross } from "../../astra/map/hatch";
import { districtLabel, labelPoint, outsideMask } from "./districtFrame";
import { PALETTE } from "../../astra/styles/palette";
import {
  contourLayers as astraContourLayers,
  LAYERS as ASTRA_LAYERS,
  STATUS_CLUSTER_MAX_ZOOM,
  STATUS_CLUSTER_RADIUS,
  statusPointLayers,
  WATCH_SOURCE_ID,
} from "./astraStyle";
import { clusterProperties } from "../../astra/map/layers";
import {
  basemapAttribution,
  BASEMAP_IDS,
  basemapLabel,
  basemapNoReplyReason,
  basemapOfflineReason,
  BASEMAP_LAYER_IMAGERY,
  BASEMAP_LAYER_LABELS,
  BASEMAP_LAYER_MASK,
  BASEMAP_MASK_OPACITY,
  BASEMAP_OFF,
  BASEMAP_SOURCE_IMAGERY,
  BASEMAP_SOURCE_LABELS,
  BASEMAP_SOURCES,
  basemapReducer,
  basemapVisible,
  defaultBasemap,
  IMAGERY_MAXZOOM,
  IMAGERY_TILES,
  LABELS_MAXZOOM,
  LABELS_TILES,
  selectedBasemap,
  type BasemapId,
  type BasemapStatus,
} from "./basemap";
import { Map as MapIcon, Satellite, SquareDashed, type LucideIcon } from "lucide-react";

/**
 * Подложка — тремя иконками в колонке под кнопками масштаба (agroai-6al.38):
 * три подписи в строку на полотне переносились и закрывали контуры. Имя
 * каждого варианта — в подсказке и доступном имени кнопки.
 */
const BASEMAP_ICON: Record<BasemapId, LucideIcon> = {
  none: SquareDashed,
  satellite: Satellite,
  labels: MapIcon,
};
import { defineMessages } from "../../i18n/define";
import { useT } from "../../i18n/hooks";
import { tr } from "../../i18n/runtime";
import styles from "./map.module.css";

const m = defineMessages({
  noWebgl: {
    id: "datamap.canvas.noWebgl",
    defaultMessage: "браузер не дал WebGL2 (выключено ускорение графики или устаревший браузер)",
    description:
      "Причина в плашке «Слой карты не отрисован: …» (хоким, менеджер): браузер не создал графический контекст WebGL2; строчная, без точки, подставляется после двоеточия",
  },
  basemapGroup: {
    id: "datamap.canvas.basemapGroup",
    defaultMessage: "Подложка карты",
    description: "aria-label группы кнопок-переключателей подложки (снимок под контурами) в углу карты",
  },
  basemapUnavailable: {
    id: "datamap.canvas.basemapUnavailable",
    defaultMessage: "Подложка недоступна: {reason}. Контуры рисуются без снимка.",
    description:
      "Уведомление в углу карты: спутниковый снимок не загрузился; {reason} — причина строчными («нет сети»); контуры при этом видны",
  },
  failure: {
    id: "datamap.canvas.failure",
    defaultMessage:
      "Слой карты не отрисован: {failure}. Пустая карта здесь НЕ означает «в этой области ничего нет» — читайте списки ниже.",
    description:
      "Красная плашка поверх карты при отказе отрисовки (хоким, менеджер): {failure} — причина; предупреждает, что пустая карта не значит «данных нет»; НЕ — заглавными, намеренно",
  },
  tipContour: {
    id: "datamap.canvas.tipContour",
    defaultMessage: "Контур {no}",
    description: "Заголовок всплывающей подсказки под курсором на карте; {no} — номер контура из данных",
  },
  tipDemo: {
    id: "datamap.canvas.tipDemo",
    defaultMessage: "ДЕМО, сейчас: {label} ({role}) · {area}",
    description:
      "Строка подсказки под курсором: культура на контуре, данные синтетические (ДЕМО заглавными); {label} — культура, {role} — её роль («основная»), {area} — площадь с «га»",
  },
  tipCropCount: {
    id: "datamap.canvas.tipCropCount",
    defaultMessage: "культур на контуре сейчас: {count}",
    description:
      "Дописка к строке «ДЕМО, сейчас» в подсказке под курсором, если на контуре несколько культур; ставится после « · »; {count} — число",
  },
  tipTier: {
    id: "datamap.canvas.tipTier",
    defaultMessage: "ярус: {tier} · {relation}",
    description:
      "Нижняя строка подсказки под курсором: ярус принятой связи геометрии и отношение; {tier} — «автоматический», {relation} — «фича = контур»; строчная",
  },
  tipPieces: {
    id: "datamap.canvas.tipPieces",
    defaultMessage: "фич: {count}",
    description:
      "Дописка к нижней строке подсказки под курсором: из скольких фич NGIS собран полигон; ставится после « · »; {count} — число",
  },
});

/**
 * Адрес воркера задаётся явно, средствами сборщика.
 *
 * Сама maplibre вычисляет его как `new URL('./maplibre-gl-worker.mjs',
 * import.meta.url)`. В dev Vite переносит модуль в `.vite/deps`, где соседнего
 * файла нет; в сборке имя собрано шаблонной строкой, и ассет не эмитится. В
 * обоих случаях воркер отдаёт 404 — а карта при этом НЕ падает: она молча
 * остаётся пустой, потому что ни один источник не догружается. Пустая карта,
 * которая выглядит как «в этой области ничего нет», — ровно тот молчаливый
 * обман, которого на этом экране быть не должно.
 */
setWorkerUrl(workerUrl);

const SOURCE_DISTRICT = "district";
/** Маска «мир минус район» и двойная обводка границы (agroai-0yt.2). */
const SOURCE_DISTRICT_MASK = "district-mask";
const LAYER_DISTRICT_MASK = "district-mask";
const LAYER_DISTRICT_CASING = "district-casing";
const LAYER_DISTRICT_LINE = "district-line";
/** Затемнение вне района: без подложки фон и так пуст, на снимке — заметно. */
const DISTRICT_MASK_OPACITY = { none: 0, imagery: 0.6 } as const;
const SOURCE_MAHALLAS = "mahallas";
const SOURCE_CONTOURS = "contours";
const LAYER_CONTOURS_FILL = "contours-fill";
const LAYER_HIGHLIGHT = "contours-highlight";
/** Обводка фокуса уровня: контуры хозяйства, на котором стоит кадр. */
const LAYER_FOCUS = "contours-focus";

/** Заливка вне фокуса. Не «спрятать»: остальной район остаётся виден. */
const FILL_OPACITY = 0.55;
const FILL_OPACITY_OUT_OF_FOCUS = 0.16;

/** Отступ рамки первого кадра, чтобы граница района не липла к краю. */
const FIT_PADDING = 24;
/** Панорама и зум идут пачками; хвост движения ждём, чтобы не слать лишнего. */
const MOVE_DEBOUNCE_MS = 200;
/** Потолок зума при наведении кадра Astra на набор контуров. */
const ASTRA_FIT_MAX_ZOOM = 14;

/**
 * Цвета слоёв берутся из токенов дизайн-системы: maplibre не понимает `var()`,
 * а сырой hex в коде — дефект. Проба настоящим элементом разворачивает любую
 * цепочку псевдонимов и отдаёт готовый `rgb(...)`.
 */
const BASE_TOKENS = [
  "--canvas",
  "--card",
  "--ink",
  "--deep",
  "--sage",
  "--line-strong",
  "--gis-selection",
  "--gis-missing",
] as const;

type TokenName = (typeof BASE_TOKENS)[number] | TierColorToken;

/**
 * Цвета обводок приходят ИЗ РЕЕСТРА ярусов, а не перечисляются здесь: список
 * в этом файле устарел бы ровно так же, как устарели три слоя обводки, из-за
 * которых ярусы merge и mn_group остались без единой собственной черты.
 */
const TOKEN_NAMES: readonly TokenName[] = [...BASE_TOKENS, ...TIER_COLOR_TOKENS];

function resolveTokens(host: HTMLElement): Record<TokenName, string> {
  const probe = document.createElement("span");
  probe.style.display = "none";
  host.appendChild(probe);
  const entries = TOKEN_NAMES.map((name) => {
    probe.style.color = `var(${name})`;
    return [name, getComputedStyle(probe).color];
  });
  probe.remove();
  return Object.fromEntries(entries) as Record<TokenName, string>;
}

/**
 * Сервер отдаёт валидный GeoJSON, но в контракте OpenAPI геометрия описана
 * свободным объектом — структурного соответствия отсюда не вывести.
 */
function asGeoJson<P>(data: LayerData<P>): FeatureCollection {
  return data as unknown as FeatureCollection;
}

/**
 * Есть ли у браузера WebGL2.
 *
 * Без него maplibre НЕ бросает исключение: конструктор пишет ошибку в консоль
 * и возвращает недостроенную карту без рисовальщика. Её `remove()` при
 * размонтировании падал `TypeError: …reading 'destroy'` (приёмка фазы 1,
 * `/data-map`) — а сам экран до того стоял пустым, как «в области ничего
 * нет». Поэтому проверка идёт ДО конструктора, и отказ называется словами.
 */
export function webgl2Available(doc: Document = document): boolean {
  try {
    const canvas = doc.createElement("canvas");
    return Boolean(canvas.getContext("webgl2"));
  } catch {
    return false;
  }
}

export const noWebgl = (): string => tr(m.noWebgl);

/**
 * Снять карту без исключения. Недостроенная карта (контекст WebGL не
 * создался после проверки — например, потерян между проверкой и
 * конструктором) рисовальщика не имеет, и штатный `remove()` на ней падает.
 */
export function disposeMap(map: { remove(): void }): void {
  if (!(map as { painter?: unknown }).painter) return;
  map.remove();
}

/** Размер кадра в пикселях: то, от чего зависит подгонка рамки. */
export interface FrameSize {
  width: number;
  height: number;
}

/**
 * Подогнать ли кадр заново после смены размера контейнера (agroai-6al.42).
 *
 * Рамка подгоняется под размер кадра В МОМЕНТ подгонки: экран, у которого
 * колонка карты дорастает до своей ширины после монтирования (реестр
 * контуров, разборы с боковой карточкой), оставлял контуры в углу кадра.
 * Подгоняем заново, только пока человек сам не двигал карту — его кадр
 * важнее рамки страницы, — и только на настоящем размере: свёрнутый
 * (нулевой) контейнер рамку не разрушает, а ждёт.
 */
export function refitOnResize(prev: FrameSize, next: FrameSize, userMoved: boolean): boolean {
  if (userMoved) return false;
  if (next.width <= 0 || next.height <= 0) return false;
  return prev.width !== next.width || prev.height !== next.height;
}

function emptyStyle(): StyleSpecification {
  // Фон не слоем, а фоном контейнера из токенов: пустой стиль оставляет канву
  // прозрачной, и подложка приходит из CSS вместе со всей палитрой продукта.
  return { version: 8, sources: {}, layers: [] };
}

const NO_HIGHLIGHT: FilterSpecification = ["==", ["get", "contour_id"], -1];

/** Фильтр набора контуров фокуса. Пустой набор — не рисуем ничего. */
function focusFilter(ids: ReadonlyArray<number>): FilterSpecification {
  if (ids.length === 0) return NO_HIGHLIGHT;
  return ["in", ["get", "contour_id"], ["literal", [...ids]]];
}

/**
 * Прозрачность заливки при фокусе уровня.
 *
 * Контуры вне фокуса ПРИГЛУШАЮТСЯ, а не исчезают: «карта хозяйства», на
 * которой соседей нет вовсе, читается как «вокруг ничего не зарегистрировано»
 * — ровно тот молчаливый обман, которого этот экран не допускает.
 */
function fillOpacity(
  ids: ReadonlyArray<number>,
): DataDrivenPropertyValueSpecification<number> {
  if (ids.length === 0) return FILL_OPACITY;
  return [
    "case",
    ["in", ["get", "contour_id"], ["literal", [...ids]]],
    FILL_OPACITY,
    FILL_OPACITY_OUT_OF_FOCUS,
  ];
}

/** Заливка по выбранной тематике. Запасной цвет один и тот же у обеих. */
function fillExpression(
  thematic: Thematic,
  palette: ReadonlyArray<MassifPaletteEntry>,
  cropPalette: ReadonlyArray<CropPaletteEntry>,
  missing: string,
) {
  return thematic === "crop_demo"
    ? cropColorExpression(cropPalette, missing)
    : massifColorExpression(palette, missing);
}

function highlightFilter(contourId: number): FilterSpecification {
  return ["==", ["get", "contour_id"], contourId];
}

interface TipState {
  x: number;
  y: number;
  /** Подсказка не должна выезжать за полотно — у краёв она разворачивается. */
  flipX: boolean;
  flipY: boolean;
  props: ContourFeatureProps;
}

export interface MapCanvasProps {
  district: LayerData<ReferenceProps>;
  mahallas: LayerData<ReferenceProps>;
  contours: LayerData<ContourFeatureProps>;
  /** Рамка первого кадра; смена контекста анализа может её обновить. */
  fitBbox: Bbox;
  palette: ReadonlyArray<MassifPaletteEntry>;
  /** ДЕМО. Палитра культур — вход тематики по синтетической детализации. */
  cropPalette: ReadonlyArray<CropPaletteEntry>;
  /**
   * Что красит заливка. Выбор делает экран; полотно только применяет — но
   * выражение собирает здесь, потому что запасной цвет берётся из токенов,
   * а токены умеет читать только этот компонент.
   */
  thematic: Thematic;
  massifName: (massifId: number) => string | null;
  onViewChange: (view: MapView) => void;
  /**
   * Нажатие по контуру. Наружу уходят ВСЕ свойства фичи, а не один
   * идентификатор: превью показывает то же, что подсказка под курсором, и
   * второй запрос ради уже пришедших величин был бы лишним.
   */
  onPick: (props: ContourFeatureProps) => void;
  /**
   * Можно ли двигать камеру. `false` — обзорный кадр: панорама и зум выключены,
   * подсказка и переход по клику остаются. Это не «упрощение ради красоты»:
   * обзор загружает район ОДНИМ запросом и не досылает области, поэтому
   * разрешить зум значило бы показывать старую выдачу как текущую.
   */
  interactive?: boolean;
  /**
   * Подсветка ИЗВНЕ: строка таблицы-компаньона под курсором или в фокусе.
   *
   * Карта и таблица — два вида одной выдачи, и наведение в одном обязано быть
   * видно в другом. Наведение мышью по полотну имеет приоритет: пока курсор на
   * контуре, внешняя подсветка ждёт.
   */
  highlightId?: number | null;
  /**
   * Фокус уровня: контуры хозяйства, на котором стоит кадр. Остальные
   * приглушаются, но остаются на карте — «карта хозяйства» без соседей
   * читалась бы как «вокруг ничего нет».
   */
  focusIds?: ReadonlyArray<number>;
  /**
   * Визуальный язык Astra. Есть — контуры рисуются слоями `astraStyle.ts`
   * (заливка по слою Astra, штриховки, обводки), а подсказку собирает экран
   * Astra. Режим читается на монтировании, как и режим камеры.
   */
  astra?: AstraLayers;
  /** Подложка при открытии; по умолчанию — настройка `VITE_MAP_BASEMAP`. */
  basemap?: BasemapId;
  /**
   * Показывать переключатель подложки (по умолчанию да). Мини-кадр превью
   * его прячет: подложка при этом остаётся той, что задана `basemap`.
   */
  basemapSwitch?: boolean;
}

/** Идентификатор фичи в режиме Astra: строковое свойство `id`. */
export type AstraFeatureProps = Record<string, unknown> & { id: string };

export interface AstraLayers {
  /** Выражение `fill-color` из `buildPaint` или `SUPPRESSED_FILL`. */
  fillColor: unknown;
  /** 45° штриховка «ожидается» — только на слое «Статус работы». */
  pending: boolean;
  /** Медная обводка кандидата — только на слое «Геометрия». */
  candidate: boolean;
  /** Янтарь наблюдения и точки — только на слое «Карантин». */
  watch: boolean;
  /** Показывать только эти контуры; `null` — все. */
  filterIds: ReadonlyArray<string> | null;
  hatchIds: ReadonlyArray<string>;
  /** Тёмная обводка отмеченных контуров (второй признак слоя страницы). */
  ringIds?: ReadonlyArray<string>;
  /** Пунктир вместо сплошной обводки (например, «после срока»). */
  ringDashed?: boolean;
  selectedId: string | null;
  workStatusById?: Readonly<Record<string, string>>;
  quarantineById?: Readonly<Record<string, string>>;
  /**
   * Значения прочих слоёв (вода, произвольные значения) — feature-state `v`
   * (agroai-6al.1). Заливку по нему собирает `buildPaint`.
   */
  valueById?: Readonly<Record<string, string>>;
  /** Точки статуса (центры контуров, `properties.severity`). */
  watchPoints: FeatureCollection;
  /**
   * Показывать точки статуса и их кластеры. Не задано — как раньше: точки
   * видны вместе с обводкой наблюдения (`watch`).
   */
  points?: boolean;
  /** Обводки ярусов связи канона — есть только у канонической геометрии. */
  tiers: boolean;
  renderTip: (props: AstraFeatureProps) => ReactNode;
  onSelect: (id: string | null) => void;
  /** Первый кадр с данными реально нарисован. */
  onPainted?: () => void;
}

function astraIdFilter(ids: ReadonlyArray<string> | null): FilterSpecification | null {
  if (!ids) return null;
  return ["in", ["get", "id"], ["literal", [...ids]]];
}

const IS_CANDIDATE: FilterSpecification = [
  "==",
  ["coalesce", ["get", "geometry_status"], "none"],
  "candidate",
];

/** Стабильная ссылка: пустой фокус не должен перезапускать эффекты. */
const EMPTY_FOCUS: ReadonlyArray<number> = [];

export function MapCanvas({
  district,
  mahallas,
  contours,
  fitBbox,
  palette,
  cropPalette,
  thematic,
  massifName,
  onViewChange,
  onPick,
  interactive = true,
  highlightId = null,
  focusIds = EMPTY_FOCUS,
  astra,
  basemap,
  basemapSwitch = true,
}: MapCanvasProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const colorRef = useRef<Record<TokenName, string> | null>(null);
  const [ready, setReady] = useState(false);
  const [tip, setTip] = useState<TipState | null>(null);
  const [astraTip, setAstraTip] = useState<{
    x: number;
    y: number;
    flipX: boolean;
    flipY: boolean;
    props: AstraFeatureProps;
  } | null>(null);
  const [status, dispatchBasemap] = useReducer(
    basemapReducer,
    basemap,
    (initial): BasemapStatus =>
      basemapReducer(BASEMAP_OFF, {
        type: "select",
        id: initial ?? defaultBasemap(),
        online: typeof navigator === "undefined" ? true : navigator.onLine,
      }),
  );
  const [failure, setFailure] = useState<string | null>(null);

  // Колбэки живут в ref: карта создаётся один раз, и пересоздавать её из-за
  // новой ссылки на обработчик было бы дороже всего остального на экране.
  const emitView = useRef(onViewChange);
  emitView.current = onViewChange;
  const emitPick = useRef(onPick);
  emitPick.current = onPick;
  const astraRef = useRef(astra);
  astraRef.current = astra;
  const astraMode = useRef(Boolean(astra)).current;
  const hoveredAstra = useRef<string | null>(null);
  // Состояния фич, выставленные поимённо: снимать их можно только так же.
  const wsIds = useRef<string[]>([]);
  const qIds = useRef<string[]>([]);
  const vIds = useRef<string[]>([]);

  const mountData = useRef({
    district,
    mahallas,
    contours,
    palette,
    cropPalette,
    thematic,
    focusIds,
  });
  mountData.current = {
    district,
    mahallas,
    contours,
    palette,
    cropPalette,
    thematic,
    focusIds,
  };
  const mountBbox = useRef(fitBbox);
  const appliedFit = useRef(fitBbox.join(","));
  /** Рамка, к которой кадр возвращается при смене размера (6al.42). */
  const latestFit = useRef(fitBbox);
  latestFit.current = fitBbox;
  /** Человек двигал карту сам: смена размера его кадр не трогает. */
  const userMoved = useRef(false);
  const hovered = useRef<number | null>(null);
  /** Подсветка от таблицы-компаньона: к ней возвращаемся, когда курсор ушёл. */
  const external = useRef<number | null>(highlightId);
  // Режим камеры читается на монтировании: карта создаётся один раз, и менять
  // его на лету незачем — экран выбирает режим, а не переключает его.
  const cameraFixed = useRef(!interactive);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    if (!webgl2Available()) {
      setFailure(noWebgl());
      return;
    }

    const color = resolveTokens(container);
    colorRef.current = color;
    let map: MapLibreMap;
    try {
      map = new MapLibreMap({
        container,
        style: emptyStyle(),
        bounds: mountBbox.current,
        fitBoundsOptions: { padding: FIT_PADDING, ...(astraMode ? { maxZoom: ASTRA_FIT_MAX_ZOOM } : {}) },
        // Атрибуция подложки — своя строка на полотне (см. разметку ниже):
        // контрол maplibre принёс бы оформление мимо дизайн-системы.
        attributionControl: false,
        // Холст читается обратно (`toDataURL`): отчёт ИИ печатает карту
        // картинкой (agroai-6al.57, журнал №605).
        canvasContextAttributes: { preserveDrawingBuffer: true },
      });
    } catch (cause) {
      setFailure(cause instanceof Error ? cause.message : String(cause));
      return;
    }
    if (!(map as unknown as { painter?: unknown }).painter) {
      // Контекст WebGL не создался уже в конструкторе: карта недостроена.
      setFailure(noWebgl());
      return;
    }
    mapRef.current = map;
    // Отказ слоя (не догрузился воркер, не собрался источник) иначе выглядит
    // как «в этой области ничего нет» — самая опасная из возможных ошибок
    // экрана. Поэтому он объявляется, а не остаётся в консоли. Отказ
    // ПОДЛОЖКИ — другое дело: он снимает фон, а не карту.
    map.on("error", (event) => {
      const sourceId = (event as { sourceId?: string }).sourceId;
      if (sourceId && BASEMAP_SOURCES.has(sourceId)) {
        // Текст ошибки загрузчика с адресом тайла человеку ничего не говорит
        // (и длиной ломает плашку): причина называется словами, а подробность
        // остаётся в консоли для разработчика.
        console.warn("подложка: тайл не загрузился", event.error);
        dispatchBasemap({
          type: "tile-error",
          message: navigator.onLine ? basemapNoReplyReason() : basemapOfflineReason(),
        });
        return;
      }
      setFailure(String(event.error?.message ?? event.error));
    });
    map.on("sourcedata", (event) => {
      if (event.sourceId && BASEMAP_SOURCES.has(event.sourceId) && event.tile) {
        dispatchBasemap({ type: "tile-loaded" });
      }
    });
    if (cameraFixed.current) {
      // Обзорный кадр: район целиком и ровно та выдача, которая под ним лежит.
      for (const handler of [
        map.dragPan,
        map.scrollZoom,
        map.boxZoom,
        map.dragRotate,
        map.touchZoomRotate,
        map.doubleClickZoom,
        map.keyboard,
      ]) {
        handler.disable();
      }
    } else {
      map.addControl(new NavigationControl({ showCompass: false }), "top-right");
    }

    let moveTimer: number | undefined;
    const publishView = () => {
      emitView.current({
        bbox: boundsToBbox(map.getBounds()),
        zoom: viewZoom(map.getZoom()),
      });
    };

    map.on("load", () => {
      const initial = mountData.current;
      map.addSource(SOURCE_DISTRICT, {
        type: "geojson",
        data: asGeoJson(initial.district),
      });
      map.addSource(SOURCE_MAHALLAS, {
        type: "geojson",
        data: asGeoJson(initial.mahallas),
      });
      map.addSource(SOURCE_CONTOURS, {
        type: "geojson",
        data: asGeoJson(initial.contours),
        // Язык Astra адресует контур feature-state (статус работы, карантин,
        // наведение) по стабильному строковому `id`, как в прототипе.
        ...(astraMode ? { promoteId: "id" } : {}),
      });

      // Снизу вверх: маска вне района → членение на махалли → контуры →
      // граница района (двойная обводка) → выделение. Граница стоит ПОВЕРХ
      // контуров: под заливками её внутренняя половина пропадала.
      map.addSource(SOURCE_DISTRICT_MASK, {
        type: "geojson",
        data: outsideMask(initial.district.features),
      });
      map.addLayer({
        id: LAYER_DISTRICT_MASK,
        type: "fill",
        source: SOURCE_DISTRICT_MASK,
        paint: { "fill-color": color["--ink"], "fill-opacity": DISTRICT_MASK_OPACITY.none },
      });
      map.addLayer({
        id: "mahallas-fill",
        type: "fill",
        source: SOURCE_MAHALLAS,
        paint: { "fill-color": color["--sage"], "fill-opacity": 0.3 },
      });
      map.addLayer({
        id: "mahallas-line",
        type: "line",
        source: SOURCE_MAHALLAS,
        paint: { "line-color": color["--line-strong"], "line-width": 1 },
      });
      const astraAtMount = astraRef.current;
      if (astraMode && astraAtMount) {
        const pending = hatch45(PALETTE.active);
        const demo = hatchCross(PALETTE.muted);
        if (pending) map.addImage(HATCH_PENDING, pending);
        if (demo) map.addImage(HATCH_DEMO, demo);
        for (const layer of astraContourLayers(astraAtMount.fillColor)) {
          map.addLayer(layer as never);
        }
        // Ярусы связи канона — поверх заливки Astra, под наведением: это
        // сведение о геометрии, которое у канонических контуров есть всегда.
        for (const layer of tierLineLayers(color)) {
          map.addLayer(
            {
              id: layer.id,
              type: "line",
              source: SOURCE_CONTOURS,
              filter: layer.filter,
              paint: layer.paint,
              layout: { visibility: astraAtMount.tiers ? "visible" : "none" },
            },
            ASTRA_LAYERS.hover,
          );
        }
        // Точки статуса собираются в кластеры на отдалении; кластер знает
        // счёт каждой тяжести и красится худшей (`layers.ts`).
        map.addSource(WATCH_SOURCE_ID, {
          type: "geojson",
          data: astraAtMount.watchPoints,
          cluster: true,
          clusterMaxZoom: STATUS_CLUSTER_MAX_ZOOM,
          clusterRadius: STATUS_CLUSTER_RADIUS,
          clusterProperties: clusterProperties() as never,
        });
        for (const layer of statusPointLayers()) {
          map.addLayer(layer as never);
        }
        // «Первый кадр нарисован» — когда контуры легли на полотно, а не
        // когда стихло ВСЁ (`idle` ждёт и тайлы подложки, и любую анимацию):
        // медленный провайдер снимков держал скелет поверх готовых контуров,
        // и кадр выглядел серым полем (журнал №1391).
        let painted = false;
        const markPainted = () => {
          if (painted) return;
          painted = true;
          map.off("sourcedata", onContoursData);
          astraRef.current?.onPainted?.();
        };
        const onContoursData = (event: { sourceId?: string }) => {
          if (event.sourceId === SOURCE_CONTOURS && map.isSourceLoaded(SOURCE_CONTOURS)) {
            map.once("render", markPainted);
          }
        };
        map.on("sourcedata", onContoursData);
        map.once("idle", markPainted);
      } else {
        map.addLayer({
          id: LAYER_CONTOURS_FILL,
          type: "fill",
          source: SOURCE_CONTOURS,
          paint: {
            "fill-color": fillExpression(
              initial.thematic,
              initial.palette,
              initial.cropPalette,
              color["--gis-missing"],
            ),
            "fill-opacity": fillOpacity(initial.focusIds),
          },
        });

        // Ярус связи — отдельным слоем на ярус: `line-dasharray` не бывает
        // data-driven, а различать штриховкой надёжнее, чем оттенком поверх
        // и без того цветной заливки.
        //
        // Слои строятся ОБХОДОМ РЕЕСТРА, а не перечислением. Пока их было три
        // штуки в коде, принятые ярусы merge и mn_group приезжали в каждой
        // выдаче и не получали на карте ни одной черты — 249 фич, которые
        // невозможно отличить глазом, потому что заливка-то рисуется. Последний
        // слой обхода ловит ярус, которого в реестре нет: незнакомое рисуется
        // как незнакомое, а не пропадает.
        for (const layer of tierLineLayers(color)) {
          map.addLayer({
            id: layer.id,
            type: "line",
            source: SOURCE_CONTOURS,
            filter: layer.filter,
            paint: layer.paint,
          });
        }

        // Фокус уровня стоит ПОД подсветкой наведения: они говорят о разном —
        // «вот земля этого хозяйства» и «вот контур под курсором».
        map.addLayer({
          id: LAYER_FOCUS,
          type: "line",
          source: SOURCE_CONTOURS,
          filter: focusFilter(initial.focusIds),
          paint: {
            "line-color": color["--gis-selection"],
            "line-width": 2,
            "line-dasharray": [3, 2],
          },
        });

        map.addLayer({
          id: LAYER_HIGHLIGHT,
          type: "line",
          source: SOURCE_CONTOURS,
          filter: NO_HIGHLIGHT,
          paint: { "line-color": color["--gis-selection"], "line-width": 3 },
        });
      }

      // Двойная обводка (casing): светлое гало читается на тёмном снимке,
      // тёмная сердцевина — на светлом. Под наведением и выделением.
      const underSelection = astraMode
        ? map.getLayer(ASTRA_LAYERS.hover) ? ASTRA_LAYERS.hover : undefined
        : map.getLayer(LAYER_FOCUS) ? LAYER_FOCUS : undefined;
      map.addLayer(
        {
          id: LAYER_DISTRICT_CASING,
          type: "line",
          source: SOURCE_DISTRICT,
          layout: { "line-join": "round" },
          paint: {
            "line-color": color["--card"],
            "line-width": ["interpolate", ["linear"], ["zoom"], 8, 4, 13, 7],
            "line-opacity": 0.9,
            "line-blur": 0.5,
          },
        },
        underSelection,
      );
      map.addLayer(
        {
          id: LAYER_DISTRICT_LINE,
          type: "line",
          source: SOURCE_DISTRICT,
          layout: { "line-join": "round" },
          paint: {
            "line-color": color["--ink"],
            "line-width": ["interpolate", ["linear"], ["zoom"], 8, 1.5, 13, 2.5],
          },
        },
        underSelection,
      );

      setReady(true);
      // Камеру задал конструктор, движения не было — первый кадр объявляем сами.
      publishView();
    });

    map.on("moveend", () => {
      window.clearTimeout(moveTimer);
      moveTimer = window.setTimeout(publishView, MOVE_DEBOUNCE_MS);
    });

    // Движение человека (а не программная подгонка) несёт исходное событие.
    map.on("movestart", (event) => {
      if ((event as { originalEvent?: unknown }).originalEvent) userMoved.current = true;
    });

    // Кадр дорос или сжался после монтирования — рамку подгоняем заново
    // (agroai-6al.42). `trackResize` maplibre только растягивает полотно и
    // оставляет центр и масштаб прежними, отсюда контуры «в углу».
    const sizeOf = (): FrameSize => ({ width: container.clientWidth, height: container.clientHeight });
    let lastSize = sizeOf();
    let resizeFrame = 0;
    const resizer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(() => {
            window.cancelAnimationFrame(resizeFrame);
            resizeFrame = window.requestAnimationFrame(() => {
              const next = sizeOf();
              const refit = refitOnResize(lastSize, next, userMoved.current);
              lastSize = next;
              if (!refit) return;
              map.resize();
              map.fitBounds(latestFit.current, {
                padding: FIT_PADDING,
                animate: false,
                ...(astraMode ? { maxZoom: ASTRA_FIT_MAX_ZOOM } : {}),
              });
            });
          });
    resizer?.observe(container);

    const showAstraTip = (event: MapLayerMouseEvent) => {
      const feature = event.features?.[0];
      const raw = feature?.properties?.id;
      if (!feature || raw === undefined || raw === null) return;
      const id = String(raw);
      const canvas = map.getCanvas();
      if (hoveredAstra.current !== id) {
        if (hoveredAstra.current !== null) {
          map.setFeatureState({ source: SOURCE_CONTOURS, id: hoveredAstra.current }, { hover: false });
        }
        map.setFeatureState({ source: SOURCE_CONTOURS, id }, { hover: true });
        hoveredAstra.current = id;
        canvas.style.cursor = "pointer";
      }
      setAstraTip({
        x: event.point.x,
        y: event.point.y,
        flipX: event.point.x > canvas.clientWidth - 260,
        flipY: event.point.y > canvas.clientHeight - 160,
        props: { ...(feature.properties as Record<string, unknown>), id },
      });
    };

    const hideAstraTip = () => {
      if (hoveredAstra.current !== null) {
        map.setFeatureState({ source: SOURCE_CONTOURS, id: hoveredAstra.current }, { hover: false });
        hoveredAstra.current = null;
      }
      map.getCanvas().style.cursor = "";
      setAstraTip(null);
    };

    const showTip = (event: MapLayerMouseEvent) => {
      const feature = event.features?.[0];
      if (!feature) return;
      const props = feature.properties as ContourFeatureProps;
      const canvas = map.getCanvas();
      if (hovered.current !== props.contour_id) {
        hovered.current = props.contour_id;
        map.setFilter(LAYER_HIGHLIGHT, highlightFilter(props.contour_id));
        canvas.style.cursor = "pointer";
      }
      setTip({
        x: event.point.x,
        y: event.point.y,
        flipX: event.point.x > canvas.clientWidth - 260,
        flipY: event.point.y > canvas.clientHeight - 160,
        props,
      });
    };

    const hideTip = () => {
      hovered.current = null;
      map.getCanvas().style.cursor = "";
      // Курсор ушёл — возвращаем подсветку строки таблицы, а не гасим всё:
      // иначе выбранная клавиатурой строка теряется от одного движения мыши.
      map.setFilter(
        LAYER_HIGHLIGHT,
        external.current === null ? NO_HIGHLIGHT : highlightFilter(external.current),
      );
      setTip(null);
    };

    if (astraMode) {
      map.on("mousemove", ASTRA_LAYERS.fill, showAstraTip);
      map.on("mouseleave", ASTRA_LAYERS.fill, hideAstraTip);
      map.on("click", ASTRA_LAYERS.fill, (event) => {
        // Нажатие по кластеру приближает, а не выбирает контур под ним.
        if (map.queryRenderedFeatures(event.point, { layers: [ASTRA_LAYERS.statusCluster] }).length > 0) {
          return;
        }
        const raw = event.features?.[0]?.properties?.id;
        astraRef.current?.onSelect(raw === undefined || raw === null ? null : String(raw));
      });
      map.on("click", ASTRA_LAYERS.statusCluster, (event) => {
        const feature = event.features?.[0];
        const clusterId = feature?.properties?.cluster_id;
        if (!feature || clusterId === undefined || feature.geometry.type !== "Point") return;
        const center = feature.geometry.coordinates as [number, number];
        const source = map.getSource(WATCH_SOURCE_ID) as GeoJSONSource | undefined;
        void source?.getClusterExpansionZoom(clusterId).then((zoom) => {
          map.easeTo({ center, zoom: zoom + 0.25 });
        });
      });
      map.on("mouseenter", ASTRA_LAYERS.statusCluster, () => {
        map.getCanvas().style.cursor = "zoom-in";
      });
      map.on("mouseleave", ASTRA_LAYERS.statusCluster, () => {
        map.getCanvas().style.cursor = "";
      });
    } else {
      map.on("mousemove", LAYER_CONTOURS_FILL, showTip);
      map.on("mouseleave", LAYER_CONTOURS_FILL, hideTip);
      map.on("click", LAYER_CONTOURS_FILL, (event) => {
        const feature = event.features?.[0];
        if (!feature) return;
        emitPick.current(feature.properties as ContourFeatureProps);
      });
    }

    // Сеть пропала — подложка честно снимается, карта остаётся.
    const offline = () => dispatchBasemap({ type: "tile-error", message: basemapOfflineReason() });
    window.addEventListener("offline", offline);

    return () => {
      window.clearTimeout(moveTimer);
      window.cancelAnimationFrame(resizeFrame);
      resizer?.disconnect();
      window.removeEventListener("offline", offline);
      setReady(false);
      mapRef.current = null;
      disposeMap(map);
    };
  }, []);

  // Данные слоёв обновляются на месте: пересборка стиля стоила бы полного
  // перерисовывания карты на каждой панораме.
  useEffect(() => {
    const source = mapRef.current?.getSource(SOURCE_CONTOURS) as
      | GeoJSONSource
      | undefined;
    if (ready && source) source.setData(asGeoJson(contours));
  }, [ready, contours]);

  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map) return;
    (map.getSource(SOURCE_DISTRICT) as GeoJSONSource | undefined)?.setData(
      asGeoJson(district),
    );
    (map.getSource(SOURCE_DISTRICT_MASK) as GeoJSONSource | undefined)?.setData(
      outsideMask(district.features),
    );
    (map.getSource(SOURCE_MAHALLAS) as GeoJSONSource | undefined)?.setData(
      asGeoJson(mahallas),
    );
  }, [ready, district, mahallas]);

  useEffect(() => {
    const map = mapRef.current;
    const color = colorRef.current;
    if (!ready || !map || !color || astraMode) return;
    map.setPaintProperty(
      LAYER_CONTOURS_FILL,
      "fill-color",
      fillExpression(thematic, palette, cropPalette, color["--gis-missing"]),
    );
  }, [ready, thematic, palette, cropPalette, astraMode]);

  // Подсветка извне. Наведение мышью главнее: пока курсор стоит на контуре,
  // строка таблицы не перебивает то, что человек видит под рукой.
  useEffect(() => {
    external.current = highlightId;
    const map = mapRef.current;
    if (!ready || !map || hovered.current !== null || astraMode) return;
    map.setFilter(
      LAYER_HIGHLIGHT,
      highlightId === null ? NO_HIGHLIGHT : highlightFilter(highlightId),
    );
  }, [ready, highlightId, astraMode]);

  // Фокус уровня: обводка набора и приглушение остального.
  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map || astraMode) return;
    map.setFilter(LAYER_FOCUS, focusFilter(focusIds));
    map.setPaintProperty(LAYER_CONTOURS_FILL, "fill-opacity", fillOpacity(focusIds));
  }, [ready, focusIds, astraMode]);

  /* ---------------------------------------------------- язык Astra */

  const astraFill = astra?.fillColor;
  const astraPending = astra?.pending ?? false;
  const astraCandidate = astra?.candidate ?? false;
  const astraWatch = astra?.watch ?? false;
  const astraTiers = astra?.tiers ?? false;
  const astraFilterIds = astra?.filterIds ?? null;
  const astraHatchIds = astra?.hatchIds;
  const astraRingIds = astra?.ringIds;
  const astraRingDashed = astra?.ringDashed ?? false;
  const astraSelected = astra?.selectedId ?? null;
  const astraWs = astra?.workStatusById;
  const astraQ = astra?.quarantineById;
  const astraWatchPoints = astra?.watchPoints;
  const astraPoints = astra?.points ?? astraWatch;
  const astraV = astra?.valueById;

  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map || !astraMode) return;
    map.setPaintProperty(ASTRA_LAYERS.fill, "fill-color", astraFill as never);
    map.setLayoutProperty(ASTRA_LAYERS.pending, "visibility", astraPending ? "visible" : "none");
    // Медная сетка кандидатов — только там, где геометрия и есть предмет
    // разговора: на остальных слоях она спорит с легендой слоя.
    map.setLayoutProperty(ASTRA_LAYERS.candidate, "visibility", astraCandidate ? "visible" : "none");
    map.setLayoutProperty(ASTRA_LAYERS.watch, "visibility", astraWatch ? "visible" : "none");
    map.setLayoutProperty(ASTRA_LAYERS.watchDot, "visibility", astraPoints ? "visible" : "none");
    map.setLayoutProperty(ASTRA_LAYERS.statusCluster, "visibility", astraPoints ? "visible" : "none");
    for (const layer of tierLineLayers(colorRef.current ?? ({} as Record<TokenName, string>))) {
      map.setLayoutProperty(layer.id, "visibility", astraTiers ? "visible" : "none");
    }
  }, [ready, astraMode, astraFill, astraPending, astraCandidate, astraWatch, astraTiers, astraPoints]);

  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map || !astraMode) return;
    const base = astraIdFilter(astraFilterIds);
    for (const layer of [
      ASTRA_LAYERS.fill,
      ASTRA_LAYERS.line,
      ASTRA_LAYERS.silent,
      ASTRA_LAYERS.hover,
      ASTRA_LAYERS.pending,
      ASTRA_LAYERS.watch,
    ]) {
      map.setFilter(layer, base);
    }
    map.setFilter(
      ASTRA_LAYERS.candidate,
      (base ? ["all", base, IS_CANDIDATE] : IS_CANDIDATE) as FilterSpecification,
    );
  }, [ready, astraMode, astraFilterIds]);

  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map || !astraMode) return;
    map.setFilter(ASTRA_LAYERS.demo, astraIdFilter(astraHatchIds ?? []));
  }, [ready, astraMode, astraHatchIds]);

  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map || !astraMode || !map.getLayer(ASTRA_LAYERS.ring)) return;
    map.setFilter(ASTRA_LAYERS.ring, astraIdFilter(astraRingIds ?? []));
    map.setPaintProperty(ASTRA_LAYERS.ring, 'line-dasharray', astraRingDashed ? [1.5, 1.2] : [1, 0]);
  }, [ready, astraMode, astraRingIds, astraRingDashed]);

  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map || !astraMode) return;
    const filter: FilterSpecification = ["==", ["get", "id"], astraSelected ?? "__none__"];
    map.setFilter(ASTRA_LAYERS.select, filter);
    map.setFilter(ASTRA_LAYERS.selectHalo, filter);
  }, [ready, astraMode, astraSelected]);

  // Статус работы и карантин — feature-state по id контура. Снимается ровно
  // то, что было выставлено: иначе состояние прошлой работы протекло бы.
  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map || !astraMode) return;
    for (const id of wsIds.current) {
      map.removeFeatureState({ source: SOURCE_CONTOURS, id }, "ws");
    }
    wsIds.current = [];
    for (const [id, ws] of Object.entries(astraWs ?? {})) {
      map.setFeatureState({ source: SOURCE_CONTOURS, id }, { ws });
      wsIds.current.push(id);
    }
  }, [ready, astraMode, astraWs, contours]);

  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map || !astraMode) return;
    for (const id of qIds.current) {
      map.removeFeatureState({ source: SOURCE_CONTOURS, id }, "q");
    }
    qIds.current = [];
    for (const [id, q] of Object.entries(astraQ ?? {})) {
      map.setFeatureState({ source: SOURCE_CONTOURS, id }, { q });
      qIds.current.push(id);
    }
  }, [ready, astraMode, astraQ, contours]);

  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map || !astraMode) return;
    for (const id of vIds.current) {
      map.removeFeatureState({ source: SOURCE_CONTOURS, id }, "v");
    }
    vIds.current = [];
    for (const [id, v] of Object.entries(astraV ?? {})) {
      map.setFeatureState({ source: SOURCE_CONTOURS, id }, { v });
      vIds.current.push(id);
    }
  }, [ready, astraMode, astraV, contours]);

  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map || !astraMode || !astraWatchPoints) return;
    (map.getSource(WATCH_SOURCE_ID) as GeoJSONSource | undefined)?.setData(astraWatchPoints);
  }, [ready, astraMode, astraWatchPoints]);

  /* -------------------------------------------------------- подложка */

  const visibleBasemap = basemapVisible(status) ? selectedBasemap(status) : "none";

  useEffect(() => {
    const map = mapRef.current;
    const color = colorRef.current;
    if (!ready || !map || !color) return;
    const present = Boolean(map.getSource(BASEMAP_SOURCE_IMAGERY));
    if (visibleBasemap === "none") {
      for (const id of [BASEMAP_LAYER_LABELS, BASEMAP_LAYER_MASK, BASEMAP_LAYER_IMAGERY]) {
        if (map.getLayer(id)) map.removeLayer(id);
      }
      for (const id of [BASEMAP_SOURCE_LABELS, BASEMAP_SOURCE_IMAGERY]) {
        if (map.getSource(id)) map.removeSource(id);
      }
      return;
    }
    if (!present) {
      // Подложка — самый нижний ярус: под рамкой района и всеми контурами.
      const below = map.getStyle().layers[0]?.id;
      map.addSource(BASEMAP_SOURCE_IMAGERY, {
        type: "raster",
        tiles: [IMAGERY_TILES],
        tileSize: 256,
        maxzoom: IMAGERY_MAXZOOM,
      });
      map.addLayer({ id: BASEMAP_LAYER_IMAGERY, type: "raster", source: BASEMAP_SOURCE_IMAGERY }, below);
      map.addLayer(
        {
          id: BASEMAP_LAYER_MASK,
          type: "background",
          paint: { "background-color": color["--canvas"], "background-opacity": BASEMAP_MASK_OPACITY },
        },
        below,
      );
      map.addSource(BASEMAP_SOURCE_LABELS, {
        type: "raster",
        tiles: [LABELS_TILES],
        tileSize: 256,
        maxzoom: LABELS_MAXZOOM,
      });
      // Подписи — над маской, но под данными: название места не должно
      // перекрывать контур, о котором экран говорит.
      map.addLayer({ id: BASEMAP_LAYER_LABELS, type: "raster", source: BASEMAP_SOURCE_LABELS }, below);
    }
    map.setLayoutProperty(
      BASEMAP_LAYER_LABELS,
      "visibility",
      visibleBasemap === "labels" ? "visible" : "none",
    );
  }, [ready, visibleBasemap]);

  // Рамка района под подложку: на снимке — маска вне района, без мутной
  // заливки махаллей и со светлыми линиями махаллей; без снимка — как было.
  useEffect(() => {
    const map = mapRef.current;
    const color = colorRef.current;
    if (!ready || !map || !color) return;
    const imagery = visibleBasemap !== "none";
    if (map.getLayer(LAYER_DISTRICT_MASK)) {
      map.setPaintProperty(
        LAYER_DISTRICT_MASK,
        "fill-opacity",
        imagery ? DISTRICT_MASK_OPACITY.imagery : DISTRICT_MASK_OPACITY.none,
      );
    }
    if (map.getLayer("mahallas-fill")) {
      map.setLayoutProperty("mahallas-fill", "visibility", imagery ? "none" : "visible");
    }
    if (map.getLayer("mahallas-line")) {
      map.setPaintProperty("mahallas-line", "line-color", imagery ? color["--card"] : color["--line-strong"]);
      map.setPaintProperty("mahallas-line", "line-opacity", imagery ? 0.6 : 1);
      map.setPaintProperty("mahallas-line", "line-width", imagery ? 0.75 : 1);
    }
  }, [ready, visibleBasemap]);

  // Подпись района. Глифов в стиле нет (см. шапку файла), поэтому подпись —
  // HTML-маркер в центре рамки района, а не символьный слой. Только на
  // полноразмерной карте: в маленьком кадре обзора она закрыла бы данные.
  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map || !interactive) return;
    const at = labelPoint(district.features);
    const text = districtLabel(district.features[0]?.properties?.name);
    if (!at || !text) return;
    const element = document.createElement("div");
    element.className = styles.districtLabel ?? "";
    element.textContent = text;
    element.setAttribute("aria-hidden", "true");
    const marker = new Marker({ element }).setLngLat(at).addTo(map);
    return () => {
      marker.remove();
    };
  }, [ready, interactive, district]);

  useEffect(() => {
    const key = fitBbox.join(",");
    if (key === appliedFit.current) return;
    const map = mapRef.current;
    // Рамка, пришедшая до готовности полотна (фокус страницы дочитался
    // позже монтирования), не считается применённой: иначе кадр оставался
    // на районе, а фокус хозяйства терялся (agroai-6al.1).
    if (!ready || !map) return;
    appliedFit.current = key;
    // Новая рамка страницы — новый кадр: прежнее движение человека к нему
    // не относится, и смена размера снова подгоняет под рамку.
    userMoved.current = false;
    map.fitBounds(fitBbox, {
      padding: FIT_PADDING,
      animate: false,
      // Кадр Astra, наведённый на один контур, не проваливается в пиксели
      // одного поля: потолок зума фокуса — как в прототипе.
      ...(astraMode ? { maxZoom: ASTRA_FIT_MAX_ZOOM } : {}),
    });
  }, [ready, fitBbox, astraMode]);

  const t = useT();
  const hint =
    tip && !astraMode ? contourTip(tip.props, massifName(tip.props.massif_id)) : null;
  const choice = selectedBasemap(status);

  return (
    <div className={styles.canvas} ref={containerRef}>
      {basemapSwitch ? (
      <div
        className={`${styles.basemap} ${interactive ? styles.basemapInteractive : ""}`}
      >
        <ToggleButtonGroup
          aria-label={t(m.basemapGroup)}
          selectionMode="single"
          disallowEmptySelection
          selectedKeys={[choice]}
          onSelectionChange={(keys) => {
            const next = [...keys][0];
            if (typeof next !== "string") return;
            dispatchBasemap({
              type: "select",
              id: next as BasemapId,
              online: navigator.onLine,
            });
          }}
          className={styles.basemapGroup}
        >
          {BASEMAP_IDS.map((id) => {
            const BasemapIcon = BASEMAP_ICON[id];
            return (
              <ToggleButton
                key={id}
                id={id}
                className={styles.basemapButton}
                aria-label={basemapLabel(id)}
              >
                <span className={styles.basemapIcon} title={basemapLabel(id)}>
                  <BasemapIcon size={16} strokeWidth={1.75} aria-hidden />
                </span>
              </ToggleButton>
            );
          })}
        </ToggleButtonGroup>
        {status.kind === "unavailable" ? (
          <div className={styles.basemapNotice} role="status">
            {t(m.basemapUnavailable, { reason: status.reason })}
          </div>
        ) : null}
      </div>
      ) : null}

      {basemapVisible(status) && status.kind !== "off" ? (
        <div className={styles.attribution}>{basemapAttribution(status.id)}</div>
      ) : null}

      {astraTip && astra ? (
        <div
          className={styles.tip}
          style={{
            left: `${astraTip.x}px`,
            top: `${astraTip.y}px`,
            transform: `translate(${astraTip.flipX ? "calc(-100% - 12px)" : "12px"}, ${
              astraTip.flipY ? "calc(-100% - 12px)" : "12px"
            })`,
          }}
          aria-hidden
        >
          {astra.renderTip(astraTip.props)}
        </div>
      ) : null}
      {failure ? (
        <div className={styles.failurePlate} role="alert">
          {t(m.failure, { failure })}
        </div>
      ) : null}

      {tip && hint ? (
        <div
          className={styles.tip}
          style={{
            left: `${tip.x}px`,
            top: `${tip.y}px`,
            transform: `translate(${tip.flipX ? "calc(-100% - 12px)" : "12px"}, ${
              tip.flipY ? "calc(-100% - 12px)" : "12px"
            })`,
          }}
          // Подсказка живёт только под курсором и дублирует то, что доступно
          // в реестре: озвучивать недостижимый с клавиатуры слой незачем.
          aria-hidden
        >
          <div className={styles.tipTitle}>{t(m.tipContour, { no: hint.contourNo })}</div>
          <div className={hint.massifMissing ? styles.tipMuted : undefined}>
            {hint.massif}
          </div>
          <div className={hint.landUserMissing ? styles.tipMuted : undefined}>
            {hint.landUser}
          </div>
          <div className={hint.areaMissing ? styles.tipMuted : undefined}>
            {hint.area}
          </div>
          {hint.cropDemo ? (
            <div className={styles.tipDemo}>
              {t(m.tipDemo, {
                label: hint.cropDemo.label,
                role: hint.cropDemo.role,
                area: hint.cropDemo.area,
              })}
              {hint.cropDemo.crops > 1
                ? ` · ${t(m.tipCropCount, { count: hint.cropDemo.crops })}`
                : ""}
            </div>
          ) : null}
          <div className={styles.tipFoot}>
            {t(m.tipTier, { tier: hint.tier, relation: hint.relation })}
            {hint.pieces > 1 ? ` · ${t(m.tipPieces, { count: hint.pieces })}` : ""}
          </div>
          {hint.shared ? (
            <div className={styles.tipShared}>{hint.shared}</div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
